import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuid } from "uuid";
import { API_URL, API_VERSION, DRY_RUN } from "@helpers/constants";
import { Helper, parseList, parseObject, setFields } from "@helpers/functions";
import { empty } from "@helpers/utils";
import { Time, Validateable } from "@models";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import { IGNORE_401 } from "@modules/+new/core/interceptors/http-context-tokens";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */
export class LatencyData {
	private _start: Date = new Date();
	private _end: Date = new Date();

	public setStart(start: number | Date): void {
		this._start = new Date(start);
	}

	public setEnd(end: number | Date): void {
		this._end = new Date(end);
	}

	public getAvgLatency(): number {
		return (+this._end - +this._start) / 2;
	}
}

@Injectable({
	providedIn: "root"
})
export class ApicallService {
	public static lastUserAction: Time;
	public static currentTimeStamp: number = 0;
	public static lastUserLatency: LatencyData = new LatencyData();
	private interval: any;

	private readonly v4Urls: string[] = ["paymentoptions", "user/best-rewards"];
	private readonly v3Urls: string[] = ["members", "masterdata", "ohlc", "assets/details"];
	private readonly v2Urls: string[] = [
		"activities",
		"portfolio",
		"stocks/transactions",
		"etfs/transactions",
		"national-identification-number",
		"elevio",
		"favorite-assets",
		"user/verify2FA",
		"leveraged_tokens/transactions",
		"portfolio/asset/fiats/",
		"countries",
		"asset-wallets",
		"fiat-wallets",
		"assets/transactions/cryptocoin/security_token",
		"user/download2fa-pdf/"
	];

	private readonly urls = {
		v2: this.v2Urls,
		v3: this.v3Urls,
		v4: this.v4Urls
	};

	public constructor(
		private http: HttpClient,
		private readonly coreStoreFacade: CoreStoreFacade
	) {
		ApicallService.lastUserAction = new Time();
	}

	public static getDeviceID(): string {
		const uid = Helper.getCookie("device_id");
		if (empty(uid) || uid === "null") {
			Helper.setCookie("device_id", uuid(), false);
		} else {
			const backup = Helper.getCookie("device_id");
			Helper.setCookie("device_id", "null", false);
			Helper.setCookie("device_id", backup, false);
		}
		return Helper.getCookie("device_id");
	}

	public get(url: string, option: ApicallOption = new ApicallOption()): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		const httpOptions = {
			...(option.headers && { headers: option.headers }),
			...(option.params && { params: option.params }),
			context: this.prepareHttpContext(option)
		};

		ApicallService.lastUserLatency.setStart(+new Date());
		return this.http.get(url, httpOptions).pipe(map((res) => this.handleResponse(res, option)));
	}

	public post(url: string, option: ApicallOption, receiveFullResponse: boolean = false): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		const httpOptions = {
			...(option.headers && { headers: option.headers }),
			context: this.prepareHttpContext(option)
		};

		ApicallService.lastUserLatency.setStart(+new Date());

		if (receiveFullResponse) {
			return this.http
				.post(url, JSON.stringify(option.data), { ...httpOptions, observe: "response" })
				.pipe(map((res) => res));
		}

		return this.http
			.post(url, JSON.stringify(option.data), httpOptions)
			.pipe(map((res) => this.handleResponse(res, option)));
	}

	//the above post will not allow us to send files and images because of the stringify function, the below one will do
	public postFile(url: string, option: ApicallOption): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		const httpOptions = {
			...(option.headers && { headers: option.headers }),
			context: this.prepareHttpContext(option)
		};

		ApicallService.lastUserLatency.setStart(+new Date());
		return this.http.post(url, option.data, httpOptions).pipe(map((res) => this.handleResponse(res, option)));
	}

	public put(
		url: string,
		option: ApicallOption = new ApicallOption(),
		receiveFullResponse: boolean = false
	): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		ApicallService.lastUserLatency.setStart(+new Date());

		if (receiveFullResponse) {
			return this.http
				.put(url, JSON.stringify(option.data), {
					context: this.prepareHttpContext(option),
					observe: "response"
				})
				.pipe(map((res) => res));
		}

		return this.http
			.put(url, JSON.stringify(option.data), { context: this.prepareHttpContext(option) })
			.pipe(map((res) => this.handleResponse(res, option)));
	}

	public patch(url: string, option: ApicallOption = new ApicallOption()): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		ApicallService.lastUserLatency.setStart(+new Date());
		return this.http
			.patch(url, JSON.stringify(option.data), { context: this.prepareHttpContext(option) })
			.pipe(map((res) => this.handleResponse(res, option)));
	}

	public deleteRequest(url: string, option: ApicallOption = new ApicallOption()): Observable<any[] | any> {
		url = this.checkUrl(url, option.apiVersion);
		this.preflight(option);

		ApicallService.lastUserLatency.setStart(+new Date());
		return this.http
			.delete(url, { context: this.prepareHttpContext(option) })
			.pipe(map((res) => this.handleResponse(res, option)));
	}

	public downloadFile(
		url: string,
		type: string = null,
		option: ApicallOption = new ApicallOption(),
		version: string = "v2"
	): Observable<Blob> {
		url = this.checkUrl(url, version);
		this.preflight(option);

		return this.http.get(url, { responseType: "blob", context: this.prepareHttpContext(option) });
	}

	public downloadFilePost(
		url: string,
		secret: string,
		option: ApicallOption = new ApicallOption(),
		version: string = "v2"
	): Observable<any> {
		url = this.checkUrl(url, version);
		this.preflight(option);

		return this.http.post(
			url,
			{ secret: secret },
			{ responseType: "blob", context: this.prepareHttpContext(option) }
		);
	}

	private prepareHttpContext(option: ApicallOption): HttpContext {
		return new HttpContext().set(IGNORE_401, option.ignore401);
	}

	private handleResponse(res: any, option: ApicallOption): any[] {
		const json = res;

		/**
		 * Handle the situation in which api returns null.
		 * Otherwise, json.hasOwnProperty throws an error;
		 */
		if (option.hasNullResponse) {
			return null;
		}

		if (json.hasOwnProperty("last_user_action")) {
			setFields(ApicallService.lastUserAction, json["last_user_action"]);
			if (this.interval) {
				clearInterval(this.interval);
			}
			ApicallService.currentTimeStamp = ApicallService.lastUserAction.getUnix();

			this.interval = setInterval(() => {
				ApicallService.currentTimeStamp += 1000;
			}, 1000);
		}

		ApicallService.lastUserLatency.setEnd(+new Date());

		if (option.raw) {
			return json;
		}

		const datajson = json.data;

		if (option.type === undefined || option.type === null) {
			return datajson;
		}

		// TODO still valid? shouldn't be "user_session"?
		if (datajson.type === "session") {
			this.coreStoreFacade.updateAccessToken(datajson.attributes.access_token);
		}

		let data;
		if (datajson.length === undefined) {
			data = [parseObject(datajson, option.type, option.rawCopy)];
		} else {
			data = parseList(datajson, option.type, option.rawCopy);
		}

		if (json.meta !== undefined && option.pagination) {
			return [data, json.meta];
		}
		if (json.created !== undefined && option.created) {
			return [data, json.created];
		}
		return data;
	}

	public preflight(option: ApicallOption): void {
		if (option.data instanceof Validateable) {
			option.data.clearError();
		}
	}

	private checkUrl(url: string, version: string = ""): string {
		// Fix and format url
		const apiVersion = version ? version : this.checkWhiltlist(url);
		const baseUrl = new URL(apiVersion + "/" + url, API_URL);
		const params = new URLSearchParams(baseUrl.search);

		// Enable test mode
		if (DRY_RUN && !this.dryRunException(url)) {
			params.append("dry_run", "1");
		}

		// Return result
		baseUrl.search = params.toString();
		return baseUrl.href;
	}

	private dryRunException(url: string): boolean {
		return url.startsWith("trade/offer") || url.startsWith("swap/offer");
	}

	private checkWhiltlist(url: string): string {
		for (const version of Object.keys(this.urls)) {
			if (this.urls[version].some((versioned: string) => url.indexOf(versioned) === 0)) {
				return version;
			}
		}
		return API_VERSION;
	}
}

export type ApiVersion = "" | "v1" | "v2" | "v3" | "v4" | "v5";
export type ApiParams =
	| HttpParams
	| { [p: string]: string | number | boolean | readonly (string | number | boolean)[] };

export class ApicallOption {
	public _type: any = null;
	public _pagination: boolean = false;
	public _ignore401: boolean = false;
	public _data: any = null;
	public _created: boolean = false;
	public _rawCopy: boolean = false;
	public _raw: boolean = false;
	public _hasNullResponse: boolean = false;
	public _params: ApiParams = null;
	private _apiVersion: ApiVersion = "";
	private _headers: HttpHeaders = null;

	public constructor(type: any = null, data: any = null) {
		this.type = type;
		this.data = data;
	}

	public get type(): any {
		return this._type;
	}

	public set type(value: any) {
		this._type = value;
	}

	public get pagination(): boolean {
		return this._pagination;
	}

	public set pagination(value: boolean) {
		this._pagination = value;
	}

	public get ignore401(): boolean {
		return this._ignore401;
	}

	public set ignore401(value: boolean) {
		this._ignore401 = value;
	}

	public get data(): any {
		return this._data;
	}

	public set data(value: any) {
		this._data = value;
	}

	public get created(): boolean {
		return this._created;
	}

	public set created(b: boolean) {
		this._created = b;
	}

	public get rawCopy(): boolean {
		return this._rawCopy;
	}

	public set rawCopy(value: boolean) {
		this._rawCopy = value;
	}

	public get raw(): boolean {
		return this._raw;
	}

	public set raw(value: boolean) {
		this._raw = value;
	}

	public get hasNullResponse(): boolean {
		return this._hasNullResponse;
	}

	public set hasNullResponse(value: boolean) {
		this._hasNullResponse = value;
	}

	public get apiVersion(): ApiVersion {
		return this._apiVersion;
	}

	public set apiVersion(value: ApiVersion) {
		this._apiVersion = value;
	}

	public set params(value: ApiParams) {
		this._params = value;
	}

	public get params(): ApiParams {
		return this._params;
	}

	public static create(option: { type: any; data: any }): ApicallOption {
		return new ApicallOption(option.type, option.data);
	}

	public set headers(value: HttpHeaders) {
		this._headers = value;
	}

	public get headers(): HttpHeaders {
		return this._headers;
	}
}
